import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import type { CouponContentResponse } from '../../../types/front.graphql';
import { getCouponImageUri } from '../../../utils/coupon-image-utils';
import { sortCoupons } from '../components/coupon-widget-content/coupon-widget-content.utils';
interface UseSortedCouponsProps {
    coupons?: CouponContentResponse[];
    isFocused: boolean;
    limitItem?: number;
}

export type CouponContent = CouponContentResponse & {
    imageUri?: string;
};

export function useSortedCoupons({ coupons, isFocused, limitItem }: UseSortedCouponsProps) {
    const [sortedCoupons, setSortedCoupons] = useState<CouponContent[]>([]);
    const isAddingRef = useRef(false);

    const couponsWithImage = useMemo(() => {
        if (!Array.isArray(coupons)) return [];
        return coupons.map(coupon => ({
            ...coupon,
            imageUri: getCouponImageUri(coupon),
        }));
    }, [coupons]);

    const sortedCouponsWithImage = useMemo(() => {
        return sortCoupons(couponsWithImage);
    }, [couponsWithImage]);

    useEffect(() => {
        if (isFocused) {
            if (Array.isArray(coupons)) {
                setSortedCoupons(sortedCouponsWithImage);
            } else {
                setSortedCoupons([]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused]);

    useEffect(() => {
        if (isAddingRef.current) {
            isAddingRef.current = false;
            if (Array.isArray(coupons) && sortedCoupons.length) {
                setSortedCoupons(prev =>
                    prev.map(item => {
                        const updated = coupons.find(c => c?.salesrule?.coupon_id === item?.salesrule?.coupon_id);
                        return updated ? { ...item, ...updated } : item;
                    })
                );
            }
            return;
        }
        if (Array.isArray(coupons)) {
            setSortedCoupons(sortedCouponsWithImage);
        } else {
            setSortedCoupons([]);
        }
    }, [coupons, sortedCoupons.length, sortedCouponsWithImage]);

    const markAdding = useCallback(() => {
        isAddingRef.current = true;
    }, []);
    const couponsList = useMemo(() => {
        if (limitItem) {
            return sortedCoupons.slice(0, limitItem);
        }
        return sortedCoupons;
    }, [sortedCoupons, limitItem]);
    return {
        sortedCoupons: couponsList,
        markAdding,
    };
}
